import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { dt, accentGradientDark, accentGradientDarkHover } from "../../../shared/themes/redesignTokens";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  fetchGroups,
  fetchGroupDetail,
  addMachinesToGroup,
  removeMachineFromGroup,
} from "../machineGroupsSlice";
import { MachineGroupViewModel, MachineGroupsState } from "../models/groupModels";
import { Machine } from "../models/models";
import { getMachineDisplayName } from "../../../shared/utils/machineUtils";
import useTranslation from "../../../shared/hooks/useTranslation";

// =====================================================================
//  PROPS
// =====================================================================

interface AssignGroupDialogProps {
  open: boolean;
  onClose: () => void;
  machine: Machine | null;
  onAssigned?: () => void;
}

// =====================================================================
//  COMPONENT
// =====================================================================

const AssignGroupDialog: React.FC<AssignGroupDialogProps> = ({
  open,
  onClose,
  machine,
  onAssigned,
}) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");

  const { groups, loading } = useAppSelector<MachineGroupsState>(
    (state: any) => state.machineGroups
  );

  // Track which groups this machine belongs to
  const [checkedGroupIds, setCheckedGroupIds] = useState<Set<string>>(new Set());
  const [originalGroupIds, setOriginalGroupIds] = useState<Set<string>>(new Set());
  const [detailsLoaded, setDetailsLoaded] = useState(false);

  // Load groups and determine membership on open
  useEffect(() => {
    if (open && machine) {
      setDetailsLoaded(false);
      dispatch(fetchGroups()).then(async (action) => {
        if (fetchGroups.fulfilled.match(action)) {
          const allGroups = action.payload as MachineGroupViewModel[];
          const memberOfIds = new Set<string>();

          // Check each group to see if this machine is a member
          const detailPromises = allGroups.map(async (group) => {
            const detailAction = await dispatch(fetchGroupDetail(group.id));
            if (fetchGroupDetail.fulfilled.match(detailAction)) {
              const detail = detailAction.payload;
              if (detail.machineIds.includes(machine.id)) {
                memberOfIds.add(group.id);
              }
            }
          });

          await Promise.all(detailPromises);
          setCheckedGroupIds(new Set(memberOfIds));
          setOriginalGroupIds(new Set(memberOfIds));
          setDetailsLoaded(true);
        }
      });
    }
  }, [open, machine, dispatch]);

  // Group groups by groupType for organized display
  const groupsByType = useMemo(() => {
    const map: Record<string, MachineGroupViewModel[]> = {};
    for (const group of groups) {
      if (!map[group.groupType]) {
        map[group.groupType] = [];
      }
      map[group.groupType].push(group);
    }
    return map;
  }, [groups]);

  const GROUP_TYPE_LABELS: Record<string, string> = {
    location: "groupTypeLocation",
    line: "groupTypeLine",
    department: "groupTypeDepartment",
    type: "groupTypeType",
    custom: "groupTypeCustom",
  };

  // =====================================================================
  //  HANDLERS
  // =====================================================================

  const handleToggle = (groupId: string) => {
    setCheckedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!machine) return;

    // Determine adds and removes
    const toAdd = Array.from(checkedGroupIds).filter(
      (id) => !originalGroupIds.has(id)
    );
    const toRemove = Array.from(originalGroupIds).filter(
      (id) => !checkedGroupIds.has(id)
    );

    // Execute additions
    for (const groupId of toAdd) {
      await dispatch(
        addMachinesToGroup({ groupId, machineIds: [machine.id] })
      );
    }

    // Execute removals
    for (const groupId of toRemove) {
      await dispatch(
        removeMachineFromGroup({ groupId, machineId: machine.id })
      );
    }

    if (onAssigned) onAssigned();
    onClose();
  };

  const hasChanges = useMemo(() => {
    if (checkedGroupIds.size !== originalGroupIds.size) return true;
    const ids = Array.from(checkedGroupIds);
    for (const id of ids) {
      if (!originalGroupIds.has(id)) return true;
    }
    return false;
  }, [checkedGroupIds, originalGroupIds]);

  // =====================================================================
  //  RENDER
  // =====================================================================

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: dt.radiusLg,
          fontFamily: dt.font,
          "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 700,
          fontSize: "18px",
          fontFamily: dt.font,
          color: dt.textPrimary,
          pb: 1,
        }}
      >
        {translate("assignToGroup")}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 1 }}>
        {/* Machine name */}
        {machine && (
          <Box
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: dt.textSecondary,
              mb: 2,
              padding: "8px 12px",
              backgroundColor: dt.inputBg,
              borderRadius: dt.radiusSm,
            }}
          >
            {getMachineDisplayName(machine)}
          </Box>
        )}

        {/* Loading state */}
        {!detailsLoaded && (
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              color: dt.textMuted,
              fontSize: "13px",
            }}
          >
            {tCommon("status.loading")}
          </Box>
        )}

        {/* Group checkboxes by type */}
        {detailsLoaded && groups.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              color: dt.textMuted,
              fontSize: "13px",
            }}
          >
            {translate("noGroupsYet")}
          </Box>
        )}

        {detailsLoaded &&
          Object.entries(groupsByType).map(([groupType, typeGroups]) => (
            <Box key={groupType} sx={{ mb: 2 }}>
              {/* Section header */}
              <Box
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "1.5px",
                  color: dt.textMuted,
                  fontFamily: dt.font,
                  mb: 1,
                  px: "4px",
                }}
              >
                {translate(GROUP_TYPE_LABELS[groupType] || groupType)}
              </Box>

              {/* Checkboxes */}
              {typeGroups.map((group) => (
                <Box
                  key={group.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderRadius: dt.radiusSm,
                    padding: "2px 4px",
                    transition: "background-color 0.2s",
                    "&:hover": { backgroundColor: dt.rowHover },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checkedGroupIds.has(group.id)}
                        onChange={() => handleToggle(group.id)}
                        size="small"
                        sx={{
                          color: dt.textMuted,
                          "&.Mui-checked": { color: group.color || dt.blue },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Box
                          sx={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: group.color || dt.blue,
                            flexShrink: 0,
                          }}
                        />
                        <Box
                          sx={{
                            fontSize: "13px",
                            fontWeight: 400,
                            color: dt.textPrimary,
                          }}
                        >
                          {group.name}
                        </Box>
                        <Box
                          sx={{
                            fontSize: "11px",
                            fontFamily: dt.fontMono,
                            color: dt.textMuted,
                            backgroundColor: dt.inputBg,
                            padding: "1px 6px",
                            borderRadius: "8px",
                          }}
                        >
                          {group.machineCount}
                        </Box>
                      </Box>
                    }
                    sx={{ flex: 1, ml: 0 }}
                  />
                </Box>
              ))}
            </Box>
          ))}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            color: dt.textSecondary,
          }}
        >
          {tCommon("buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={!hasChanges || loading}
          onClick={handleSave}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: dt.radiusSm,
            background: accentGradientDark,
            boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
            "&:hover": {
              background: accentGradientDarkHover,
            },
            "&.Mui-disabled": {
              background: "#E5E7EB",
            },
          }}
        >
          {tCommon("buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignGroupDialog;
